import * as R from 'ramda'
import logger from 'util/logger'

export const resolveAliasKey = s => {
  const hasAlias = R.includes(':as', s)

  if (hasAlias) {
    const alias = s.split(':as').map(x => x.trim())[1]
    return alias
  }

  return s
}

const hasKeysArray = v => !!v.keys && R.is(Array, v.keys)

export const treeToDb = (query, data) => {
  const id = R.prop(':db/id', data)

  if (!R.is(Array, query)) {
    logger.error('treeToDb failure:', { query, data })
    return
  }

  return R.reduce(
    (acc, key) => {
      if (R.is(String, key)) {
        const path = [id, key]
        const val = R.propOr(null, key, data)
        const idProp = R.prop(':db/id')

        if (id) {
          if (R.is(Array, val) && idProp(R.head(val))) {
            return R.assocPath(path, R.map(idProp, val), acc)
          }

          if (R.is(Object, val) && idProp(val)) {
            return R.assocPath(path, idProp(val), acc)
          }

          return R.assocPath(path, val, acc)
        }
      }

      if (R.is(Object, key)) {
        return R.reduce(
          (innerAcc, [joinKey, joinVal]) => {
            const isCollection =
              R.has('@withMeta', joinVal) || R.has('withMeta', joinVal)

            const resolvedJoinKey = resolveAliasKey(joinKey)
            const resolvedJoinVal = hasKeysArray(joinVal)
              ? joinVal.keys
              : joinVal

            const path = [id, resolvedJoinKey]
            const foundVal = R.propOr(null, resolvedJoinKey, data)

            if (R.is(Array, foundVal) || isCollection) {
              const _data = R.propOr(foundVal, 'data', foundVal)

              if (id) {
                const joinIds = R.all(R.has(':db/id'), foundVal)
                  ? R.pluck(':db/id', foundVal)
                  : foundVal
                const val = {
                  ...(R.is(Array, _data) && {
                    data: joinVal.keys ? R.pluck(':db/id', _data) : _data
                  }),
                  ...R.omit(['keys'], joinVal),
                  ...(!R.is(Array, foundVal) && R.omit(['data'], foundVal))
                }
                const updateVal = isCollection ? val : joinIds
                innerAcc = R.assocPath(path, updateVal, innerAcc)
              }

              if (R.is(Array, resolvedJoinVal)) {
                const subTrees = _data
                  ? _data.map(found => {
                      return treeToDb(resolvedJoinVal, found)
                    })
                  : []

                subTrees.forEach(subTree => {
                  if (subTree) {
                    innerAcc = R.mergeDeepLeft(innerAcc, subTree)
                  }
                })
              }

              return innerAcc
            }

            if (R.is(Object, foundVal)) {
              const joinId = R.prop(':db/id', foundVal)

              if (id) {
                const updateVal = joinId ? joinId : foundVal
                innerAcc = R.assocPath(path, updateVal, innerAcc)
              }

              if (joinId) {
                const subTree = treeToDb(resolvedJoinVal, foundVal)

                if (subTree) {
                  innerAcc = R.mergeDeepLeft(innerAcc, subTree)
                }
              }

              return innerAcc
            }

            if (id) {
              return R.assocPath(path, foundVal, innerAcc)
            }

            return innerAcc
          },
          acc,
          R.toPairs(key)
        )
      }

      return acc
    },
    {},
    query
  )
}

export const dbToTree = (eid, query = [], db) => {
  return R.reduce(
    (acc, attr) => {
      if (R.is(String, attr)) {
        const entity = R.prop(eid, db)
        const foundVal = R.prop(attr, entity)

        if (!foundVal && attr === ':db/id') {
          return R.assoc(attr, eid, acc)
        }

        if (foundVal === null) {
          return acc
        }

        return R.assoc(attr, foundVal, acc)
      }

      if (R.is(Object, attr)) {
        const joins = R.reduce(
          (_acc, [joinKey, joinAttrs]) => {
            const resolvedJoinKey = resolveAliasKey(joinKey)
            const resolvedJoinAttrs = hasKeysArray(joinAttrs)
              ? joinAttrs.keys
              : joinAttrs

            const foundVal = R.path([eid, resolvedJoinKey], db)

            if (R.is(Array, resolvedJoinAttrs)) {
              const data = R.prop('data', foundVal)
              if (R.is(Array, foundVal) || data) {
                const subTrees = R.map(
                  _eid => dbToTree(_eid, resolvedJoinAttrs, db),
                  data || foundVal
                )

                if (data) {
                  return R.assoc(
                    resolvedJoinKey,
                    R.assoc('data', subTrees, foundVal),
                    _acc
                  )
                }

                return R.assoc(resolvedJoinKey, subTrees, _acc)
              }

              if (R.is(String, foundVal)) {
                const subTree = dbToTree(foundVal, resolvedJoinAttrs, db)
                return R.assoc(resolvedJoinKey, subTree, _acc)
              }

              if (R.is(Number, foundVal)) {
                const subTree = dbToTree(foundVal, resolvedJoinAttrs, db)
                return R.assoc(resolvedJoinKey, subTree, _acc)
              }
            }

            if (R.is(Object, resolvedJoinAttrs)) {
              if (!R.isNil(foundVal)) {
                return R.assoc(resolvedJoinKey, foundVal, _acc)
              }
            }

            return _acc
          },
          {},
          R.toPairs(attr)
        )

        return R.mergeDeepLeft(joins, acc)
      }

      return acc
    },
    {},
    query
  )
}

export const mergeFragments = fragments => {
  const [objects, strs] = R.partition(R.is(Object), R.unnest(fragments))

  const object = R.reduce(
    R.mergeDeepWith((left, right) => {
      const isArray = R.is(Array)
      if (isArray(left) && isArray(right)) {
        return mergeFragments([left, right])
      }
      return right
    }),
    {},
    objects
  )

  const attrs = R.uniq(strs)

  if (R.isEmpty(object)) {
    return attrs
  }

  return [...attrs, object]
}

export const withoutStoredAttributes = (eid, query = [], db) => {
  return R.reduce(
    (acc, attr) => {
      if (R.is(String, attr)) {
        const hasAttr = R.hasPath([eid, attr], db)
        if (!hasAttr || attr === ':db/id') {
          return R.append(attr, acc)
        }
      }

      if (R.is(Object, attr)) {
        const requiredJoins = R.reduce(
          (_acc, [joinKey, joinVal]) => {
            const resolvedJoinKey = resolveAliasKey(joinKey)
            const resolvedJoinVal = hasKeysArray(joinVal)
              ? joinVal.keys
              : joinVal

            const foundVal = R.path([eid, resolvedJoinKey], db)
            const hasFoundVal = R.hasPath([eid, resolvedJoinKey], db)

            if (!hasFoundVal) {
              return R.assoc(joinKey, joinVal, _acc)
            }

            if (R.is(Array, resolvedJoinVal)) {
              const isCollection =
                R.has('@withMeta', joinVal) || R.has('withMeta', joinVal)

              if (R.is(Array, foundVal) || isCollection) {
                const subTrees = R.pipe(
                  R.reduce(
                    (__acc, id) => [
                      ...__acc,
                      ...withoutStoredAttributes(id, resolvedJoinVal, db)
                    ],
                    []
                  ),
                  R.uniq
                )(isCollection ? foundVal.data || [] : foundVal)

                if (R.without([':db/id'], subTrees).length) {
                  const newJoinVal = R.is(Array, joinVal.keys)
                    ? { ...joinVal, keys: subTrees }
                    : subTrees
                  return R.assoc(joinKey, newJoinVal, _acc)
                }

                return _acc
              }

              const ref = R.prop(foundVal, db)
              const hasRef = R.has(foundVal, db)

              if (hasRef) {
                const subTree = withoutStoredAttributes(
                  ref[':db/id'],
                  resolvedJoinVal,
                  db
                )

                if (R.without([':db/id'], subTree).length) {
                  return R.assoc(joinKey, subTree, _acc)
                }
              }
            }

            return _acc
          },
          {},
          R.toPairs(attr)
        )

        if (!R.isEmpty(requiredJoins)) {
          return R.append(requiredJoins, acc)
        }
      }

      return acc
    },
    [],
    query
  )
}
